import React, { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';
import LearningOutcomeSelector from './LearningOutcomeSelector';
import SearchableDropdown from '../common/SearchableDropdown';
import PremiumTooltip from '../common/PremiumTooltip';
import {
    PlusIcon,
    TrashIcon,
    XMarkIcon,
    PencilSquareIcon,
    SparklesIcon,
    CheckCircleIcon as CheckCircleIconOutline
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid';

const QuizBuilder = ({ lessonId, courseId, quiz, onClose, onSave }) => {
    const { get, post, put, del } = useApi();
    const { showToast } = useAppState();
    const [submitting, setSubmitting] = useState(false);
    const [activeTab, setActiveTab] = useState('settings'); // 'settings' or 'questions'
    const [savedQuiz, setSavedQuiz] = useState(quiz);

    const [strands, setStrands] = useState([]);
    const [selectedStrand, setSelectedStrand] = useState(quiz?.strand_id || null);
    const [selectedOutcomes, setSelectedOutcomes] = useState(
        quiz?.tested_outcomes_detail?.length > 0
            ? quiz.tested_outcomes_detail.map(o => ({
                id: o.id,
                description: o.description,
                code: o.code,
                sub_strand_id: o.sub_strand_id
            }))
            : (quiz?.learning_outcome ? [{
                id: quiz.learning_outcome,
                description: quiz.learning_outcome_description || 'Selected Outcome',
                sub_strand_id: quiz.sub_strand_id
            }] : [])
    );

    const [formData, setFormData] = useState({
        title: quiz?.title || '',
        instructions: quiz?.instructions || '',
        time_limit_minutes: quiz?.time_limit_minutes || 0,
        max_attempts: quiz?.max_attempts || 1,
        is_published: quiz?.is_published || false,
        due_date: quiz?.due_date ? new Date(new Date(quiz.due_date).getTime() - (new Date().getTimezoneOffset() * 60000)).toISOString().slice(0, 16) : '',
        manual_total_marks: quiz?.manual_total_marks || '',
    });

    const [questions, setQuestions] = useState(quiz?.questions || []);
    const [editingQuestion, setEditingQuestion] = useState(null);

    // AI Draft Questions state
    const [draftLoading, setDraftLoading] = useState(false);
    const [draftQuestions, setDraftQuestions] = useState([]);
    const [showDraftPanel, setShowDraftPanel] = useState(false);
    const [acceptedDraftIds, setAcceptedDraftIds] = useState(new Set());

    useEffect(() => {
        if (quiz?.questions) {
            setQuestions(quiz.questions);
        }

        // Sync outcomes when quiz prop changes or is loaded
        if (quiz) {
            if (quiz.tested_outcomes_detail?.length > 0) {
                setSelectedOutcomes(quiz.tested_outcomes_detail.map(o => ({
                    id: o.id,
                    description: o.description,
                    code: o.code,
                    sub_strand_id: o.sub_strand_id
                })));
            } else if (quiz.learning_outcome) {
                setSelectedOutcomes([{
                    id: quiz.learning_outcome,
                    description: quiz.learning_outcome_description || 'Selected Outcome',
                    sub_strand_id: quiz.sub_strand_id
                }]);
            }

            if (quiz.strand_id) {
                setSelectedStrand(quiz.strand_id);
            }
        }

        // Fetch strands for selector
        const fetchStrands = async () => {
            try {
                const response = await get(`/api/cbc/learning-areas/${courseId}/strands/`);
                setStrands(Array.isArray(response) ? response : []);
            } catch (error) {
                console.error('Error fetching strands:', error);
            }
        };
        fetchStrands();
    }, [quiz, courseId, get]);

    const handleSettingsChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleTabChange = async (newTab) => {
        // If switching to questions tab and quiz not yet saved, auto-save it first
        if (newTab === 'questions' && !savedQuiz) {
            // Validate required fields
            if (!formData.title || selectedOutcomes.length === 0) {
                showToast('Please complete quiz configuration first (title and learning outcomes required)', 'error');
                return;
            }

            setSubmitting(true);
            try {
                const payload = {
                    ...formData,
                    lesson: lessonId,
                    learning_area: courseId,
                    learning_outcome: selectedOutcomes[0]?.id,
                    tested_outcomes: selectedOutcomes.map(o => o.id)
                };

                const response = await post(`/teachers/api/lessons/${lessonId}/quizzes/`, payload);
                setSavedQuiz(response);
                showToast('Quiz saved! Now add your questions.');

                // Set submitting to false BEFORE switching tabs or calling parent
                setSubmitting(false);
                setActiveTab('questions');

                // Defer parent refresh to avoid disrupting current modal state immediately
                setTimeout(() => onSave(), 0);
            } catch (error) {
                console.error('Error saving quiz:', error);
                showToast('Failed to save quiz', 'error');
                setSubmitting(false);
            }
        } else {
            setActiveTab(newTab);
        }
    };

    const handleSaveSettings = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const payload = {
                ...formData,
                lesson: lessonId,
                learning_area: courseId,
                learning_outcome: selectedOutcomes[0]?.id, // Fallback
                tested_outcomes: selectedOutcomes.map(o => o.id)
            };

            if (savedQuiz) {
                const response = await put(`/teachers/api/quizzes/${savedQuiz.id}/`, payload);
                setSavedQuiz(response);
                showToast('Quiz configuration updated successfully!');
                onClose();
            } else {
                const response = await post(`/teachers/api/lessons/${lessonId}/quizzes/`, payload);
                setSavedQuiz(response);
                showToast('Quiz saved! Switch to Question Bank to add questions.');
                setActiveTab('questions');
            }
            onSave(); // Refresh course data
        } catch (error) {
            console.error('Error saving quiz:', error);
            showToast('Failed to save quiz', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleAddQuestion = () => {
        setEditingQuestion({
            prompt: '',
            question_type: 'multiple_choice',
            choices: ['', ''], // Start with minimum 2 choices
            correct_answer: 0,
            points: 1,
            order: questions.length + 1
        });
    };

    const handleSaveQuestion = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const quizId = savedQuiz.id;
            const isNew = !editingQuestion.id;

            if (!isNew) {
                await put(`/teachers/api/quizzes/${quizId}/questions/${editingQuestion.id}/`, editingQuestion);
                showToast('Question updated!');
            } else {
                await post(`/teachers/api/quizzes/${quizId}/questions/`, editingQuestion);
                showToast('Question added! You can now add another.');
            }

            // Refresh local questions
            const updatedQuiz = await get(`/teachers/api/quizzes/${quizId}/`);
            setQuestions(updatedQuiz.questions || []);

            if (isNew) {
                // Immediately prepare a fresh form for the next question
                setEditingQuestion({
                    prompt: '',
                    question_type: 'multiple_choice',
                    choices: ['', ''],
                    correct_answer: 0,
                    points: 1,
                    order: (updatedQuiz.questions?.length || 0) + 1
                });
            } else {
                // If it was an edit, return to the list
                setEditingQuestion(null);
            }

            // Refresh parent state if needed, but carefully
            if (onSave) {
                setTimeout(() => onSave(), 0);
            }
        } catch (error) {
            console.error('Error saving question:', error);
            showToast('Failed to save question', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteQuestion = async (qId) => {
        if (!window.confirm('Delete this question?')) return;
        try {
            await del(`/teachers/api/quizzes/${savedQuiz.id}/questions/${qId}/`);
            showToast('Question deleted');
            const updatedQuiz = await get(`/teachers/api/quizzes/${savedQuiz.id}/`);
            setQuestions(updatedQuiz.questions || []);
            onSave();
        } catch (error) {
            showToast('Failed to delete question', 'error');
        }
    };

    // AI: Get the sub_strand_id from selected outcomes with robust fallback
    const aiSubStrandId = selectedOutcomes.length > 0
        ? (selectedOutcomes[0].sub_strand_id || selectedOutcomes[0].sub_strand || selectedOutcomes[0].sub_strand_detail?.id)
        : null;
    const aiOutcomeIds = selectedOutcomes.map(o => o.id);
    const canDraftQuestions = savedQuiz && aiSubStrandId && aiOutcomeIds.length > 0;

    const handleDraftQuestions = async () => {
        if (!canDraftQuestions) {
            showToast('Please complete Assessment Configuration (select strand and outcomes) first', 'error');
            return;
        }
        setDraftLoading(true);
        setShowDraftPanel(true);
        setDraftQuestions([]);
        setAcceptedDraftIds(new Set());
        try {
            const response = await post('/teachers/api/ai/draft-questions/', {
                sub_strand_id: aiSubStrandId,
                outcome_ids: aiOutcomeIds,
                count: 5,
                difficulty: 'standard'
            });
            setDraftQuestions(response.questions || []);
        } catch (error) {
            console.error('Draft questions error:', error);
            showToast('Failed to generate questions. Please try again.', 'error');
            setShowDraftPanel(false);
        } finally {
            setDraftLoading(false);
        }
    };

    const handleAcceptDraftQuestion = async (draftQ, draftIdx) => {
        if (!savedQuiz) return;
        try {
            // Map AI draft format to quiz question format
            const correctIdx = draftQ.options.findIndex(o => o.is_correct);
            const payload = {
                prompt: draftQ.prompt,
                question_type: 'multiple_choice',
                choices: draftQ.options.map(o => o.text),
                correct_answer: correctIdx >= 0 ? correctIdx : 0,
                points: draftQ.points || 2,
                order: questions.length + 1
            };
            await post(`/teachers/api/quizzes/${savedQuiz.id}/questions/`, payload);
            const updatedQuiz = await get(`/teachers/api/quizzes/${savedQuiz.id}/`);
            setQuestions(updatedQuiz.questions || []);
            setAcceptedDraftIds(prev => new Set([...prev, draftIdx]));
            showToast('Question added to bank!');
            if (onSave) setTimeout(() => onSave(), 0);
        } catch (error) {
            showToast('Failed to add question', 'error');
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[200] p-4 backdrop-blur-sm">
            <div className="bg-white rounded-[2.5rem] shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-in zoom-in-95 duration-300">
                {/* Header */}
                <div className="px-8 py-5 border-b border-gray-100 flex items-center justify-between bg-slate-50">
                    <div>
                        <h2 className="text-2xl font-black text-[#18216D] tracking-tight">
                            {savedQuiz ? `Editing: ${savedQuiz.title}` : 'New Quiz'}
                        </h2>
                    </div>
                    <div className="flex items-center gap-4">
                        {/* AI Draft button — only shown on questions tab */}
                        {activeTab === 'questions' && (
                            <PremiumTooltip
                                text={canDraftQuestions ? 'Generate AI questions for selected outcomes' : 'Select strand & outcomes in configuration first'}
                                position="bottom"
                            >
                                <button
                                    type="button"
                                    onClick={handleDraftQuestions}
                                    disabled={!canDraftQuestions || draftLoading}
                                    className={`flex items-center gap-2 px-5 py-2.5 rounded-2xl text-[11px] font-black uppercase tracking-widest transition-all ${
                                        canDraftQuestions
                                            ? 'bg-amber-400 hover:bg-amber-500 text-[#18216D] shadow-md hover:scale-[1.03] active:scale-[0.97] border border-amber-300'
                                            : 'bg-slate-200 text-slate-400 cursor-not-allowed border border-slate-200'
                                    }`}
                                >
                                    <SparklesIcon className={`w-4 h-4 text-[#18216D] ${draftLoading ? 'animate-spin' : ''}`} />
                                    <span>{draftLoading ? 'Generating...' : 'Draft Questions'}</span>
                                </button>
                            </PremiumTooltip>
                        )}
                        <button onClick={onClose} className="p-3 bg-white border border-gray-200 rounded-2xl text-gray-400 hover:text-red-500 hover:border-red-100 transition-all shadow-sm">
                            <XMarkIcon className="w-6 h-6" />
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex px-8 border-b border-gray-100 space-x-8 bg-white">
                    <button
                        onClick={() => handleTabChange('settings')}
                        className={`py-4 text-xs font-black uppercase tracking-widest border-b-4 transition-all ${activeTab === 'settings' ? 'border-[#18216D] text-[#18216D]' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
                    >
                        Assessment Configuration
                    </button>
                    <button
                        onClick={() => handleTabChange('questions')}
                        className={`py-4 text-xs font-black uppercase tracking-widest border-b-4 transition-all ${activeTab === 'questions' ? 'border-[#18216D] text-[#18216D]' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
                    >
                        Question Bank ({savedQuiz ? questions.length : 0})
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
                    {activeTab === 'settings' ? (
                        <form onSubmit={handleSaveSettings} className="space-y-10 max-w-3xl">
                            {/* CBC Integration */}
                            <section className="space-y-6">
                                <div className="flex items-center gap-2">
                                    <span className="w-8 h-8 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center text-sm">1</span>
                                    <h3 className="font-black text-[#18216D] uppercase tracking-widest text-xs">Curriculum Mapping</h3>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-[11px] font-black text-[#18216D] uppercase tracking-widest mb-2 ml-1">Curriculum Strand</label>
                                        <SearchableDropdown
                                            options={strands.map(s => ({ id: s.id, name: s.name }))}
                                            selectedValues={selectedStrand ? [selectedStrand] : []}
                                            onChange={(val) => {
                                                setSelectedStrand(val);
                                                setSelectedOutcomes([]);
                                            }}
                                            placeholder="Select a strand..."
                                        />
                                    </div>

                                    {selectedStrand && (
                                        <div className="md:col-span-2">
                                            <LearningOutcomeSelector
                                                learningAreaId={courseId}
                                                strandId={selectedStrand}
                                                selectedOutcomes={selectedOutcomes}
                                                multiple={true}
                                                onChange={setSelectedOutcomes}
                                            />
                                        </div>
                                    )}
                                </div>
                            </section>

                            <section className="space-y-6">
                                <div className="flex items-center gap-2">
                                    <span className="w-8 h-8 rounded-lg bg-blue-100 text-[#18216D] flex items-center justify-center text-sm font-black">2</span>
                                    <h3 className="font-black text-[#18216D] uppercase tracking-widest text-xs">General Details</h3>
                                </div>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-[11px] font-black text-slate-700 uppercase tracking-widest mb-2 ml-1">Assessment Title</label>
                                        <input
                                            type="text" name="title" value={formData.title} onChange={handleSettingsChange}
                                            className="w-full px-5 py-4 bg-white border-2 border-slate-300 focus:border-[#18216D] rounded-2xl transition-all font-bold text-slate-900 placeholder:text-slate-500 placeholder:font-semibold outline-none shadow-sm"
                                            placeholder="Assessment Title (e.g., Mathematics End of Term Quiz)" required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[11px] font-black text-slate-700 uppercase tracking-widest mb-2 ml-1">Instructions for Scholars</label>
                                        <textarea
                                            name="instructions" value={formData.instructions} onChange={handleSettingsChange}
                                            className="w-full px-5 py-4 bg-white border-2 border-slate-300 focus:border-[#18216D] rounded-2xl transition-all font-bold text-slate-900 placeholder:text-slate-500 placeholder:font-semibold outline-none h-32 resize-none shadow-sm"
                                            placeholder="Clear instructions for the scholar..."
                                        />
                                    </div>
                                </div>
                            </section>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div>
                                    <label className="block text-[11px] font-black text-slate-700 uppercase tracking-widest mb-2 ml-1">Time Limit (Min)</label>
                                    <input
                                        type="number" name="time_limit_minutes" value={formData.time_limit_minutes} onChange={handleSettingsChange}
                                        className="w-full px-5 py-4 bg-white border-2 border-slate-300 focus:border-[#18216D] rounded-2xl transition-all font-bold text-slate-900 outline-none shadow-sm"
                                        min="0"
                                    />
                                    <p className="text-[10px] text-slate-500 mt-2 italic ml-1 font-bold">Set to 0 for unlimited time</p>
                                </div>
                                <div>
                                    <label className="block text-[11px] font-black text-slate-700 uppercase tracking-widest mb-2 ml-1">Allowed Attempts</label>
                                    <input
                                        type="number" name="max_attempts" value={formData.max_attempts} onChange={handleSettingsChange}
                                        className="w-full px-5 py-4 bg-white border-2 border-slate-300 focus:border-[#18216D] rounded-2xl transition-all font-bold text-slate-900 outline-none shadow-sm"
                                        min="1"
                                    />
                                    <p className="text-[10px] text-slate-500 mt-2 italic ml-1 font-bold">Maximum tries per scholar</p>
                                </div>

                                {/* Refined Score & Deadline Section */}
                                <div>
                                    <label className="block text-[11px] font-black text-slate-700 uppercase tracking-widest mb-2 ml-1">Assessment Deadline (Due Date)</label>
                                    <input
                                        type="datetime-local" name="due_date" value={formData.due_date} onChange={handleSettingsChange}
                                        className="w-full px-5 py-4 bg-white border-2 border-slate-300 focus:border-[#18216D] rounded-2xl transition-all font-bold text-slate-900 outline-none shadow-sm"
                                    />
                                    <p className="text-[10px] text-slate-500 mt-2 italic ml-1 font-bold">Auto-closes after this time</p>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Publish Status</label>
                                <button
                                    type="button"
                                    onClick={() => setFormData(p => ({ ...p, is_published: !p.is_published }))}
                                    className={`w-full p-4 rounded-2xl border-2 transition-all flex items-center justify-between ${formData.is_published ? 'border-amber-500 bg-amber-50/50' : 'border-slate-50 bg-slate-50 hover:border-slate-200'}`}
                                >
                                    <div className="text-left">
                                        <p className={`font-black text-xs uppercase ${formData.is_published ? 'text-amber-600' : 'text-slate-400'}`}>Publish assessment immediately</p>
                                        <p className="text-[10px] font-bold text-slate-400 opacity-70 mt-1">Scholars will see this once questions are added.</p>
                                    </div>
                                    <div className={`w-12 h-6 rounded-full p-1 transition-all ${formData.is_published ? 'bg-amber-500' : 'bg-slate-200'}`}>
                                        <div className={`w-4 h-4 bg-white rounded-full transition-all transform ${formData.is_published ? 'translate-x-6' : 'translate-x-0'}`} />
                                    </div>
                                </button>
                            </div>

                            <div className="pt-4">
                                <button
                                    type="submit" disabled={submitting}
                                    className="px-10 py-5 bg-[#18216D] text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-indigo-900/20 hover:bg-[#0D164F] hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50"
                                >
                                    {submitting ? 'Saving...' : (savedQuiz ? 'Update Configuration' : 'Save & Continue')}
                                </button>
                            </div>
                        </form>
                    ) : (
                        <div className="space-y-6">
                            {editingQuestion ? (
                                <form onSubmit={handleSaveQuestion} className="bg-slate-50 p-6 rounded-[2rem] border border-slate-100 animate-in slide-in-from-top-4 duration-300">
                                    <div className="flex items-center justify-between mb-6">
                                        <h4 className="text-lg font-black text-[#18216D] tracking-tight">
                                            {editingQuestion.id ? 'Edit Question' : 'New Multiple Choice Question'}
                                        </h4>
                                        <div className="flex items-center space-x-6">
                                            <div className="flex items-center space-x-3 bg-white px-4 py-2 rounded-2xl border border-slate-100 shadow-sm">
                                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Points:</label>
                                                <input
                                                    type="number"
                                                    value={editingQuestion.points}
                                                    onChange={e => setEditingQuestion({ ...editingQuestion, points: parseFloat(e.target.value) || 0 })}
                                                    className="w-16 bg-transparent font-black text-[#18216D] text-sm outline-none text-right"
                                                    min="0.5" step="0.5" required
                                                />
                                            </div>
                                            <button type="button" onClick={() => setEditingQuestion(null)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all">
                                                <XMarkIcon className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="space-y-8">
                                        <div>
                                            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 ml-1">Question Prompt</label>
                                            <textarea
                                                value={editingQuestion.prompt}
                                                onChange={e => setEditingQuestion({ ...editingQuestion, prompt: e.target.value })}
                                                className="w-full px-5 py-4 bg-white border-2 border-transparent focus:border-[#18216D] rounded-2xl transition-all font-bold text-gray-900 outline-none h-28 shadow-sm"
                                                placeholder="Define the problem..." required
                                            />
                                        </div>
                                        <div>
                                            <div className="flex items-center justify-between mb-4 ml-1">
                                                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest">Assessment Options & Key</label>
                                                {editingQuestion.choices.length < 6 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const newChoices = [...editingQuestion.choices, ''];
                                                            setEditingQuestion({ ...editingQuestion, choices: newChoices });
                                                        }}
                                                        className="text-[10px] font-black text-blue-600 uppercase tracking-widest hover:text-blue-700"
                                                    >
                                                        + Add Option
                                                    </button>
                                                )}
                                            </div>
                                            <div className="space-y-3">
                                                {editingQuestion.choices.map((choice, index) => (
                                                    <div key={index} className="flex items-center space-x-4 group/choice">
                                                        <button
                                                            type="button"
                                                            onClick={() => setEditingQuestion({ ...editingQuestion, correct_answer: index })}
                                                            className={`flex-shrink-0 w-10 h-10 rounded-xl transition-all flex items-center justify-center ${editingQuestion.correct_answer === index ? 'text-white bg-emerald-500 shadow-lg shadow-emerald-500/20' : 'bg-white text-slate-200 border-2 border-slate-50 hover:border-slate-200'}`}
                                                        >
                                                            {editingQuestion.correct_answer === index ? <CheckCircleIconSolid className="w-6 h-6" /> : <div className="w-4 h-4 bg-transparent border-2 border-current rounded-full" />}
                                                        </button>
                                                        <div className="flex-1 relative">
                                                            <input
                                                                type="text" value={choice}
                                                                onChange={e => {
                                                                    const newChoices = [...editingQuestion.choices];
                                                                    newChoices[index] = e.target.value;
                                                                    setEditingQuestion({ ...editingQuestion, choices: newChoices });
                                                                }}
                                                                className={`w-full px-5 py-4 border-2 rounded-2xl outline-none transition-all font-bold text-sm ${editingQuestion.correct_answer === index ? 'border-emerald-100 bg-emerald-50/30' : 'border-transparent bg-white shadow-sm'}`}
                                                                placeholder={`Option ${index + 1}`} required
                                                            />
                                                            {editingQuestion.choices.length > 2 && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        const newChoices = editingQuestion.choices.filter((_, i) => i !== index);
                                                                        let newCorrect = editingQuestion.correct_answer;
                                                                        if (newCorrect === index) newCorrect = 0;
                                                                        else if (newCorrect > index) newCorrect -= 1;
                                                                        setEditingQuestion({ ...editingQuestion, choices: newChoices, correct_answer: newCorrect });
                                                                    }}
                                                                    className="absolute right-4 top-1/2 -translate-y-1/2 p-2 text-slate-300 hover:text-red-500 opacity-0 group-hover/choice:opacity-100 transition-all"
                                                                >
                                                                    <TrashIcon className="w-4 h-4" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="flex gap-4 pt-6">
                                            <button
                                                type="submit" disabled={submitting}
                                                className="px-8 py-4 bg-[#18216D] text-white rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-[#0D164F] transition-all shadow-lg"
                                            >
                                                {submitting ? 'Saving...' : 'Save Question'}
                                            </button>
                                            <button
                                                type="button" onClick={() => setEditingQuestion(null)}
                                                className="px-8 py-4 bg-white text-slate-400 border border-slate-100 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-slate-100 transition-all"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                </form>
                                ) : (
                                    <div className="space-y-6">
                                        {/* AI Draft Questions Panel */}
                                        {showDraftPanel && (
                                            <div className="bg-gradient-to-br from-violet-50 to-indigo-50 border border-violet-200 rounded-[2rem] p-6 animate-in slide-in-from-top-4 duration-300">
                                                <div className="flex items-center justify-between mb-5">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-9 h-9 rounded-xl bg-violet-600 flex items-center justify-center shadow-lg shadow-violet-500/30">
                                                            <SparklesIcon className="w-5 h-5 text-white" />
                                                        </div>
                                                        <div>
                                                            <p className="text-xs font-black text-[#18216D] uppercase tracking-widest">AI Draft Questions</p>
                                                            <p className="text-[10px] font-bold text-slate-500 mt-0.5">Review each question — Accept or Discard</p>
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={() => { setShowDraftPanel(false); setDraftQuestions([]); }}
                                                        className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                                                    >
                                                        <XMarkIcon className="w-5 h-5" />
                                                    </button>
                                                </div>

                                                {draftLoading ? (
                                                    <div className="flex flex-col items-center justify-center py-10 gap-4">
                                                        <div className="relative">
                                                            <div className="w-12 h-12 rounded-full border-4 border-violet-200 border-t-violet-600 animate-spin" />
                                                            <SparklesIcon className="w-5 h-5 text-violet-600 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                                                        </div>
                                                        <p className="text-xs font-black text-violet-700 uppercase tracking-widest animate-pulse">Drafting questions aligned to your outcomes...</p>
                                                    </div>
                                                ) : (
                                                    <div className="space-y-4">
                                                        {draftQuestions.map((dq, idx) => (
                                                            <div
                                                                key={idx}
                                                                className={`p-5 rounded-2xl border-2 transition-all ${
                                                                    acceptedDraftIds.has(idx)
                                                                        ? 'border-emerald-200 bg-emerald-50/60 opacity-70'
                                                                        : 'border-white bg-white shadow-sm'
                                                                }`}
                                                            >
                                                                <div className="flex items-start justify-between gap-4">
                                                                    <div className="flex-1">
                                                                        <p className="text-sm font-black text-[#18216D] leading-snug">{dq.prompt}</p>
                                                                        {dq.outcome_code && (
                                                                            <span className="inline-block mt-2 px-2 py-0.5 bg-violet-100 text-violet-700 text-[9px] font-black uppercase tracking-widest rounded-lg">
                                                                                Tests: {dq.outcome_code}
                                                                            </span>
                                                                        )}
                                                                        <div className="grid grid-cols-2 gap-2 mt-4">
                                                                            {(dq.options || []).map((opt, oi) => (
                                                                                <div
                                                                                    key={oi}
                                                                                    className={`px-3 py-2 rounded-xl text-xs font-bold ${
                                                                                        opt.is_correct
                                                                                            ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                                                                                            : 'bg-slate-50 text-slate-600 border border-slate-100'
                                                                                    }`}
                                                                                >
                                                                                    {opt.is_correct && <span className="mr-1">✓</span>}
                                                                                    {opt.text}
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                        {dq.explanation && (
                                                                            <p className="mt-3 text-[10px] text-slate-500 font-medium italic border-l-2 border-violet-200 pl-3">
                                                                                {dq.explanation}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                    <div className="flex flex-col gap-2 flex-shrink-0">
                                                                        {acceptedDraftIds.has(idx) ? (
                                                                            <div className="flex items-center gap-2 px-4 py-2.5 bg-emerald-100 text-emerald-700 rounded-xl text-[10px] font-black uppercase tracking-widest">
                                                                                <CheckCircleIconOutline className="w-4 h-4" /> Added
                                                                            </div>
                                                                        ) : (
                                                                            <>
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => handleAcceptDraftQuestion(dq, idx)}
                                                                                    className="px-4 py-2.5 bg-[#18216D] text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-[#0D164F] transition-all shadow-sm"
                                                                                >
                                                                                    Accept
                                                                                </button>
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => setAcceptedDraftIds(prev => new Set([...prev, idx]))}
                                                                                    className="px-4 py-2.5 bg-white text-slate-400 border border-slate-200 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 transition-all"
                                                                                >
                                                                                    Discard
                                                                                </button>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                        {draftQuestions.length > 0 && (
                                                            <p className="text-center text-[10px] font-bold text-slate-400 pt-2">
                                                                {acceptedDraftIds.size} of {draftQuestions.length} questions accepted
                                                            </p>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        <button
                                            onClick={handleAddQuestion}
                                            className="w-full flex items-center justify-center space-x-3 p-8 border-2 border-dashed border-slate-200 bg-slate-50 rounded-[2.5rem] text-slate-400 hover:border-[#18216D] hover:text-[#18216D] hover:bg-white transition-all group shadow-sm"
                                        >
                                            <PlusIcon className="w-6 h-6 group-hover:scale-110 transition-transform" />
                                            <span className="font-black uppercase tracking-[0.2em] text-xs">Append New Question</span>
                                        </button>

                                        <div className="space-y-4">
                                            {questions.map((q, idx) => (
                                                <div key={q.id} className="flex items-center space-x-6 p-6 bg-white border border-slate-100 rounded-3xl shadow-sm hover:shadow-xl hover:shadow-indigo-900/5 transition-all group">
                                                    <div className="flex-shrink-0 text-[10px] font-black text-slate-300 uppercase tracking-widest bg-slate-50 w-12 h-12 flex items-center justify-center rounded-2xl">
                                                        #{idx + 1}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-[#18216D] font-black truncate text-sm">{q.prompt}</p>
                                                        <p className="text-[10px] text-slate-400 mt-1 uppercase font-black tracking-widest">{q.points} Point Assessment • Multiple Choice</p>
                                                    </div>
                                                    <div className="flex items-center space-x-3 transition-all">
                                                        <PremiumTooltip text="Edit Question" position="top">
                                                            <button onClick={() => setEditingQuestion(q)} className="p-3 text-[#18216D] hover:bg-[#18216D]/10 bg-slate-50 rounded-xl transition-all shadow-sm hover:shadow-md">
                                                                <PencilSquareIcon className="w-5 h-5" />
                                                            </button>
                                                        </PremiumTooltip>
                                                        <PremiumTooltip text="Delete Question" position="top">
                                                            <button onClick={() => handleDeleteQuestion(q.id)} className="p-3 text-red-600 hover:bg-red-50 bg-slate-50 rounded-xl transition-all shadow-sm hover:shadow-md">
                                                                <TrashIcon className="w-5 h-5" />
                                                            </button>
                                                        </PremiumTooltip>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
    );
};

export default QuizBuilder;
